import type { PartnerLocation } from '../../partners/types';
import type { ShippingAddress, Squad, User } from '../../../types';

export type TeacherMapFilters = {
  staffStatus: 'all' | 'active' | 'applicant' | 'offboarded';
  state: string;
  subject: string;
};

export type SquadMapFilters = {
  curriculum: string;
  season: string;
  staffingNeed: 'all' | 'needs_staff' | 'staffed';
};

export type CanonicalSquadMapRecord = {
  squad: Squad;
  location: PartnerLocation | null;
  latitude: number | null;
  longitude: number | null;
};

const clean = (value: unknown): string => String(value || '').trim();

export function shippingAddressForGeocoding(address?: ShippingAddress | null): string {
  const formatted = clean(address?.formattedAddress);
  if (formatted) return formatted;
  return [
    address?.line1,
    address?.line2,
    address?.city,
    address?.state,
    address?.zip,
    address?.country,
  ].map(clean).filter(Boolean).join(', ');
}

export function teacherZipCode(address?: ShippingAddress | null): string {
  const zip = clean(address?.zip).match(/\d{5}/);
  if (zip) return zip[0];
  const formatted = clean(address?.formattedAddress).match(/\b(\d{5})(?:-\d{4})?\b/);
  return formatted ? formatted[1] : '';
}

/** ZIP or city/state queries for map pins. Avoids street-level geocoding. */
export function teacherMapSearchQueries(address?: ShippingAddress | null): string[] {
  const zip = teacherZipCode(address);
  const city = clean(address?.city);
  const state = clean(address?.state);
  const queries: string[] = [];
  if (zip) {
    queries.push(`${zip} USA`);
    if (city && state) queries.push(`${city}, ${state} ${zip}`);
  }
  if (city && state) queries.push(`${city}, ${state}, USA`);
  else if (city) queries.push(`${city}, USA`);
  return Array.from(new Set(queries));
}

export function teacherMapAddressFingerprint(address?: ShippingAddress | null): string {
  const placeId = clean(address?.placeId);
  if (placeId) return `place:${placeId}`;
  const normalized = shippingAddressForGeocoding(address).toLowerCase().replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  let hash = 2166136261;
  for (let index = 0; index < normalized.length; index += 1) {
    hash ^= normalized.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `address:${(hash >>> 0).toString(36)}`;
}

export function storedTeacherMapCoordinates(teacher: User): { lat: number; lng: number } | null {
  const stored = teacher.teacherMapLocation;
  const fingerprint = teacherMapAddressFingerprint(teacher.shippingAddress);
  if (!stored || !fingerprint || stored.addressFingerprint !== fingerprint) return null;
  const lat = Number(stored.latitude);
  const lng = Number(stored.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

export function teacherMapState(teacher: User): string {
  return clean(teacher.shippingAddress?.state || teacher.state).toUpperCase();
}

export function teacherMatchesMapFilters(teacher: User, filters: TeacherMapFilters): boolean {
  const status = clean(teacher.staffStatus || 'active').toLowerCase();
  if (filters.staffStatus !== 'all' && status !== filters.staffStatus) return false;
  if (filters.state && teacherMapState(teacher) !== filters.state.toUpperCase()) return false;
  if (filters.subject) {
    const subjects = Array.isArray(teacher.subjects) ? teacher.subjects.map((item) => clean(item).toLowerCase()) : [];
    if (!subjects.includes(filters.subject.toLowerCase())) return false;
  }
  return true;
}

export function squadNeedsStaff(squad: Squad): boolean {
  // Leads are written to `leadTeacherId` by Ops Flows and to `regularTeacher`
  // by the admin squad details / manage-users flows; either counts as staffed.
  return !clean(squad.leadTeacherId) && !clean(squad.regularTeacher);
}

export function squadMatchesMapFilters(squad: Squad, filters: SquadMapFilters): boolean {
  const curriculum = clean(squad.curriculumId || 'music').toLowerCase();
  const season = clean(squad.termId || squad.seasonId || (squad as any).term).toLowerCase();
  if (filters.curriculum && curriculum !== filters.curriculum.toLowerCase()) return false;
  if (filters.season && season !== filters.season.toLowerCase()) return false;
  if (filters.staffingNeed === 'needs_staff' && !squadNeedsStaff(squad)) return false;
  if (filters.staffingNeed === 'staffed' && squadNeedsStaff(squad)) return false;
  return true;
}

export type SquadMapCluster = {
  key: string;
  lat: number;
  lng: number;
  records: CanonicalSquadMapRecord[];
};

export function groupSquadRecordsByMapPoint(
  records: CanonicalSquadMapRecord[],
  locationGeocodes: Record<string, { lat: number; lng: number }> = {},
): SquadMapCluster[] {
  const groups = new Map<string, SquadMapCluster>();
  records.forEach((record) => {
    const fallback = locationGeocodes[record.location?.id || ''];
    const lat = fallback?.lat ?? record.latitude;
    const lng = fallback?.lng ?? record.longitude;
    if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const key = record.location?.id || `${Number(lat).toFixed(5)},${Number(lng).toFixed(5)}`;
    const group = groups.get(key);
    if (group) group.records.push(record);
    else groups.set(key, { key, lat: Number(lat), lng: Number(lng), records: [record] });
  });
  return Array.from(groups.values());
}

export function groupTeachersByMapPoint(
  teachers: Array<{ teacher: User; lat: number; lng: number }>,
): Array<{ key: string; lat: number; lng: number; teachers: User[] }> {
  const groups = new Map<string, { key: string; lat: number; lng: number; teachers: User[] }>();
  teachers.forEach(({ teacher, lat, lng }) => {
    const key = `${lat.toFixed(4)},${lng.toFixed(4)}`;
    const group = groups.get(key);
    if (group) group.teachers.push(teacher);
    else groups.set(key, { key, lat, lng, teachers: [teacher] });
  });
  return Array.from(groups.values());
}

export function canonicalSquadMapRecords(
  squads: Squad[],
  locations: PartnerLocation[],
): CanonicalSquadMapRecord[] {
  const locationsById = new Map(locations.map((location) => [location.id, location]));
  return squads.map((squad) => {
    const locationId = clean(squad.locationId);
    const location = (locationId && locationsById.get(locationId)) || null;
    const latitude = typeof location?.latitude === 'number' && Number.isFinite(location.latitude)
      ? location.latitude
      : null;
    const longitude = typeof location?.longitude === 'number' && Number.isFinite(location.longitude)
      ? location.longitude
      : null;
    return { squad, location, latitude, longitude };
  });
}

export function squadScheduleLabel(squad: Squad): string {
  const schedule = squad.schedule || {};
  const dates = schedule.mainDate
    ? clean(schedule.mainDate)
    : [schedule.startDate, schedule.endDate].map(clean).filter(Boolean).join(' – ');
  const day = clean(schedule.dayOfWeek);
  const times = [schedule.startTime, schedule.endTime].map(clean).filter(Boolean).join(' – ');
  return [dates, day, times].filter(Boolean).join(' · ') || 'Schedule not set';
}

export function squadSeasonLabel(squad: Squad): string {
  return clean(squad.termId || squad.seasonId || (squad as any).term) || 'Season not set';
}
